import React, { useState } from "react";

import { Accordion, Button, Picto } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { Link, useNavigate } from "react-router-dom";

import { usePlanGate } from "@/hooks/usePlanGate";

import { Dot } from "@/components/atoms/Dot/Dot";
import { EntityRow } from "@/components/molecules/EntityRow/EntityRow";
import { PROJECT_LIST_METRICS } from "@/components/organisms/ProjectsCard/ProjectsCard.columns";

import { convertToTime } from "@/utils/UDates";
import { TProjectGroup } from "@/utils/UProjects";
import { cn } from "@/utils/cn";

import { TProjectClient } from "@/types";

export interface IProjectsByClientProps {
	groups: TProjectGroup[];
	/** "+ Project" on a group: its client, or null for the internal group. */
	/** Left out: no "+ Project" on the groups (the workspace is not the user's to grow). */
	onCreate?: (client: TProjectClient | null) => void;
	/**
	 * The user's own plan locks "+ Project" at its limit. False for another
	 * workspace: its plan is not the user's, the API decides there.
	 */
	planGated?: boolean;
	className?: string;
}

/**
 * The /projects page's "By client" display: one accordion (one card) per
 * client, the internal group last. The header's title is the client and
 * its figures; the link to the client page and "+ Project" sit in the
 * item's `actions`, outside the toggle. Every group starts open: the
 * state keeps the closed ones, so a group that a filter brings back
 * shows up open.
 */
/** gate.button's shape, without a plan to lock on. */
const plainButton = <T extends object>(props: T) => props;

export const ProjectsByClient: React.FC<IProjectsByClientProps> = ({
	groups,
	onCreate,
	planGated = true,
	className,
}) => {
	const navigate = useNavigate();
	const gate = usePlanGate("projects");
	const [closed, setClosed] = useState<number[]>([]);

	const toggle = (id: number) =>
		setClosed((prev) =>
			prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
		);

	return (
		<div className={cn(["flex flex-col gap-3", className])}>
			{groups.map((group) => {
				const name = group.client?.name ?? t("projects.internal");
				const newLabel = group.client
					? t("projects.group.newIn", { name })
					: t("projects.group.newInternal");

				const item = {
					value: group.id,
					ariaLabel: name,
					title: (
						<span
							className="flex items-center gap-2"
							data-testid={`project-group-${group.id}`}
						>
							<Dot
								color={group.client?.color ?? "#a3a3a3"}
								className="h-2.5 w-2.5 min-w-2.5"
							/>
							<span className="truncate">{name}</span>
							<span className="shrink-0 font-normal text-neutral-500">
								{t("projects.group.meta", {
									count: group.projects.length,
									hours: convertToTime(group.workedHours),
								})}
							</span>
						</span>
					),
					actions: (
						<>
							{group.client?.canOpen && (
								<Link
									to={`/clients/${group.client.id}`}
									aria-label={t("projects.group.openClient", {
										name,
									})}
									title={t("projects.group.openClient", {
										name,
									})}
									className="rounded p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
								>
									<Picto
										icon="externalLink"
										className="h-4 w-4"
									/>
								</Link>
							)}
							{onCreate && (
								<Button
									{...(planGated ? gate.button : plainButton)(
										{
											size: "sm" as const,
											variant: "ghost" as const,
											picto: "add" as const,
											"aria-label": newLabel,
											title: newLabel,
											onClick: () =>
												onCreate(group.client),
										}
									)}
								>
									{t("projects.label")}
								</Button>
							)}
						</>
					),
					content: (
						<div className="flex flex-col divide-y divide-neutral-100">
							{group.projects.map((project) => (
								<EntityRow
									key={project.id}
									className="px-1"
									data-testid={`project-row-${project.id}`}
									color={project.color}
									name={project.name}
									description={project.description}
									isArchived={project.isArchived}
									metrics={PROJECT_LIST_METRICS(project, {
										withClient: false,
									})}
									onClick={() =>
										navigate(`/projects/${project.id}`)
									}
								/>
							))}
						</div>
					),
				};

				return (
					<Accordion<number>
						key={group.id}
						className="bg-white"
						multiple={false}
						chevronPosition="start"
						value={closed.includes(group.id) ? null : group.id}
						onChange={() => toggle(group.id)}
						items={[item]}
					/>
				);
			})}
		</div>
	);
};
